import { useState, useEffect, useCallback } from 'react';
import { Layout } from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { 
  ArrowLeft, 
  Save, 
  Send, 
  Eye, 
  Clock, 
  Loader2,
  TestTube,
  Calendar,
  Users,
  AlertCircle,
  CheckCircle2,
  Monitor,
  Smartphone,
  Tablet,
  X
} from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import { Link } from 'react-router-dom';
import WysiwygEditor from '@/components/WysiwygEditor';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function NewsletterEditorPage() {
  const { campaignId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const isEditing = Boolean(campaignId);
  
  const [loading, setLoading] = useState(isEditing);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);
  const [showSendConfirm, setShowSendConfirm] = useState(false);
  const [showSchedule, setShowSchedule] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [previewDevice, setPreviewDevice] = useState('desktop');
  const [subscriberCount, setSubscriberCount] = useState(0);
  const [groups, setGroups] = useState([]);
  const [selectedGroupIds, setSelectedGroupIds] = useState([]);
  
  const [formData, setFormData] = useState({
    name: '',
    subject: '',
    content_html: '',
    scheduled_at: ''
  });
  
  const [testEmail, setTestEmail] = useState('');
  
  const getToken = () => localStorage.getItem('shrt_token');

  useEffect(() => {
    if (isEditing) {
      fetchCampaign();
    }
    fetchSubscriberCount();
    fetchGroups();
  }, [campaignId]);

  const fetchCampaign = async () => {
    try {
      const response = await axios.get(
        `${API_URL}/api/newsletter/campaigns/${campaignId}`,
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      const campaign = response.data.campaign;
      setFormData({
        name: campaign.name || '',
        subject: campaign.subject || '',
        content_html: campaign.content_html || '',
        scheduled_at: campaign.scheduled_at || ''
      });
    } catch (error) {
      toast.error('Kampagne nicht gefunden');
      navigate('/admin/newsletter-system');
    } finally {
      setLoading(false);
    }
  };

  const fetchSubscriberCount = async () => {
    try {
      const response = await axios.get(
        `${API_URL}/api/newsletter/subscribers?status=subscribed&limit=1`,
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      setSubscriberCount(response.data.counts?.subscribed || 0);
    } catch (error) {
      console.error('Failed to fetch subscriber count:', error);
    }
  };

  const fetchGroups = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/newsletter-groups`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      const activeGroups = (response.data || []).filter(g => g.is_active && g.active_contacts > 0);
      setGroups(activeGroups);
      
      // Auto-select "Alle Mitglieder" if coming from event page
      if (searchParams.get('preselect_all_members') === 'true') {
        const allMembersGroup = activeGroups.find(g => g.id === '__all_members__');
        if (allMembersGroup) {
          setSelectedGroupIds([allMembersGroup.id]);
        }
      }
    } catch (error) {
      console.error('Failed to fetch groups:', error);
    }
  };

  const toggleGroupSelection = (groupId) => {
    setSelectedGroupIds(prev =>
      prev.includes(groupId) ? prev.filter(id => id !== groupId) : [...prev, groupId]
    );
  };

  const getTotalRecipients = () => {
    return groups.filter(g => selectedGroupIds.includes(g.id)).reduce((sum, g) => sum + g.active_contacts, 0);
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async (andSend = false) => {
    if (!formData.name.trim() || !formData.subject.trim()) {
      toast.error('Name und Betreff sind erforderlich');
      return;
    }
    
    setSaving(true);
    
    try {
      let campaign;
      
      if (isEditing) {
        await axios.put(
          `${API_URL}/api/newsletter/campaigns/${campaignId}`,
          formData,
          { headers: { Authorization: `Bearer ${getToken()}` } }
        );
        campaign = { id: campaignId };
        toast.success('Kampagne gespeichert');
      } else {
        const response = await axios.post(
          `${API_URL}/api/newsletter/campaigns`,
          formData,
          { headers: { Authorization: `Bearer ${getToken()}` } }
        );
        campaign = response.data.campaign;
        toast.success('Kampagne erstellt');
      }
      
      if (andSend) {
        navigate(`/admin/newsletter-system/edit/${campaign.id}`);
        setShowSendConfirm(true);
      } else if (!isEditing) {
        navigate(`/admin/newsletter-system/edit/${campaign.id}`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const handleSendTest = async () => {
    if (!testEmail.trim()) {
      toast.error('Bitte geben Sie eine Test-E-Mail-Adresse ein');
      return;
    }
    
    if (!formData.subject.trim() || !formData.content_html.trim()) {
      toast.error('Betreff und Inhalt sind erforderlich');
      return;
    }
    
    setSendingTest(true);
    
    try {
      // Save first if needed
      if (!isEditing) {
        const response = await axios.post(
          `${API_URL}/api/newsletter/campaigns`,
          formData,
          { headers: { Authorization: `Bearer ${getToken()}` } }
        );
        navigate(`/admin/newsletter-system/edit/${response.data.campaign.id}`, { replace: true });
      }
      
      const sendCampaignId = campaignId || formData.id;
      
      await axios.post(
        `${API_URL}/api/newsletter/campaigns/${sendCampaignId}/send?test_email=${encodeURIComponent(testEmail)}`,
        {},
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      
      toast.success(`Test-E-Mail gesendet an ${testEmail}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Test-E-Mail konnte nicht gesendet werden');
    } finally {
      setSendingTest(false);
    }
  };

  const handleSendCampaign = async () => {
    setSending(true);
    
    try {
      // Save first
      await axios.put(
        `${API_URL}/api/newsletter/campaigns/${campaignId}`,
        formData,
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      
      // Send
      const response = await axios.post(
        `${API_URL}/api/newsletter/campaigns/${campaignId}/send`,
        { group_ids: selectedGroupIds },
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      
      toast.success(response.data.message);
      navigate('/admin/newsletter-system');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Senden');
    } finally {
      setSending(false);
      setShowSendConfirm(false);
    }
  };

  const handleSchedule = async () => {
    if (!formData.scheduled_at) {
      toast.error('Bitte wählen Sie einen Zeitpunkt');
      return;
    }
    
    setSaving(true);
    
    try {
      const updateData = {
        ...formData,
        status: 'scheduled'
      };
      
      if (isEditing) {
        await axios.put(
          `${API_URL}/api/newsletter/campaigns/${campaignId}`,
          updateData,
          { headers: { Authorization: `Bearer ${getToken()}` } }
        );
      } else {
        await axios.post(
          `${API_URL}/api/newsletter/campaigns`,
          updateData,
          { headers: { Authorization: `Bearer ${getToken()}` } }
        );
      }
      
      toast.success('Kampagne geplant');
      navigate('/admin/newsletter-system');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Planen');
    } finally {
      setSaving(false);
      setShowSchedule(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6 max-w-5xl">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/admin/newsletter-system')}
              className="text-muted-foreground hover:text-foreground"
            >
              <ArrowLeft size={20} />
            </button>
            <h1 className="font-['Oswald'] text-2xl font-bold text-foreground uppercase tracking-tight">
              {isEditing ? 'Kampagne bearbeiten' : 'Neue Kampagne'}
            </h1>
          </div>
          
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={() => setShowPreview(true)}
              disabled={!formData.content_html}
              className="gap-2 rounded-none"
            >
              <Eye size={16} />
              Vorschau
            </Button>
            <Button
              variant="outline"
              onClick={() => handleSave(false)}
              disabled={saving}
              className="gap-2 rounded-none"
            >
              {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
              Speichern
            </Button>
          </div>
        </div>

        {/* Subscriber Info */}
        <div className="bg-blue-500/10 border border-blue-500/30 p-4 flex items-center gap-3">
          <Users size={20} className="text-blue-500" />
          <p className="text-sm text-blue-400">
            Diese Kampagne wird an <strong>{subscriberCount} Abonnenten</strong> gesendet
          </p>
        </div>

        {/* Form */}
        <div className="bg-card border border-border p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                Kampagnenname *
              </Label>
              <Input
                value={formData.name}
                onChange={(e) => handleChange('name', e.target.value)}
                placeholder="z.B. Newsletter März 2026"
                className="bg-background border-border rounded-none"
              />
              <p className="text-xs text-muted-foreground">Nur für interne Verwendung</p>
            </div>
            
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                E-Mail Betreff *
              </Label>
              <Input
                value={formData.subject}
                onChange={(e) => handleChange('subject', e.target.value)}
                placeholder="Betreffzeile der E-Mail..."
                className="bg-background border-border rounded-none"
              />
            </div>
          </div>
          
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">
              E-Mail Inhalt *
            </Label>
            <WysiwygEditor
              value={formData.content_html}
              onChange={(value) => handleChange('content_html', value)}
              placeholder="Schreiben Sie Ihren Newsletter..."
            />
          </div>
        </div>

        {/* Test Email */}
        <div className="bg-card border border-border p-6">
          <h3 className="font-semibold mb-4 flex items-center gap-2">
            <TestTube size={18} />
            Test-E-Mail senden
          </h3>
          <div className="flex gap-2">
            <Input
              type="email"
              value={testEmail}
              onChange={(e) => setTestEmail(e.target.value)}
              placeholder="test@example.com"
              className="bg-background border-border rounded-none flex-1 max-w-sm"
            />
            <Button
              onClick={handleSendTest}
              disabled={sendingTest || !formData.subject || !formData.content_html}
              variant="outline"
              className="gap-2 rounded-none"
            >
              {sendingTest ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
              Test senden
            </Button>
          </div>
          <p className="text-xs text-muted-foreground mt-2">
            Senden Sie zuerst einen Test, um die E-Mail zu überprüfen
          </p>
        </div>

        {/* Send Actions */}
        <div className="bg-card border border-border space-y-0">
          <h3 className="font-semibold px-6 py-4 border-b border-border">Kampagne versenden</h3>
          
          {/* Group Selection - same style as simple newsletter */}
          <div className="border-b border-border">
            <div className="flex items-center justify-between px-5 py-2 bg-muted/20 border-b border-border/50">
              <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Senden an:</span>
              <Link to="/admin/newsletter-groups" className="text-xs text-primary hover:underline">Gruppen verwalten</Link>
            </div>
            <div className="max-h-[240px] overflow-y-auto divide-y divide-border/30">
              {groups.map(group => (
                <label
                  key={group.id}
                  className={`flex items-center gap-3 px-5 py-3 cursor-pointer transition-colors ${
                    selectedGroupIds.includes(group.id) ? 'bg-primary/5' : 'hover:bg-muted/30'
                  }`}
                >
                  <Checkbox
                    checked={selectedGroupIds.includes(group.id)}
                    onCheckedChange={() => toggleGroupSelection(group.id)}
                    className="h-5 w-5"
                  />
                  <span className={`flex-1 text-sm ${selectedGroupIds.includes(group.id) ? 'font-semibold' : ''}`}>
                    {group.name}
                  </span>
                  {group.is_system && <span className="text-[10px] bg-primary/15 text-primary px-1.5 py-0.5 uppercase font-medium">Auto</span>}
                  <span className="text-sm text-muted-foreground tabular-nums">{group.active_contacts.toLocaleString('de-CH')} Kontakte</span>
                </label>
              ))}
            </div>
            <div className="flex items-center justify-between px-5 py-2 bg-muted/20 border-t border-border/50">
              <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Empfänger:</span>
              <span className="text-sm font-semibold tabular-nums">
                {selectedGroupIds.length > 0 ? (
                  <>~{getTotalRecipients().toLocaleString('de-CH')}<span className="text-xs text-muted-foreground font-normal ml-1">(Duplikate werden entfernt)</span></>
                ) : (
                  <span className="text-muted-foreground font-normal text-xs">Bitte Gruppen wählen</span>
                )}
              </span>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="p-6 space-y-4">
            {!showSendConfirm && !showSchedule ? (
              <div className="flex gap-3">
                <Button
                  onClick={() => setShowSendConfirm(true)}
                  disabled={!formData.subject || !formData.content_html || selectedGroupIds.length === 0}
                  className="gap-2 rounded-none flex-1"
                >
                  <Send size={16} />
                  Jetzt senden
                </Button>
                <Button
                  onClick={() => setShowSchedule(true)}
                  variant="outline"
                  disabled={!formData.subject || !formData.content_html}
                  className="gap-2 rounded-none"
                >
                  <Clock size={16} />
                  Planen
                </Button>
              </div>
            ) : showSchedule ? (
              <div className="bg-blue-500/10 border border-blue-500/30 p-4 space-y-4">
                <div className="flex items-center gap-2 text-blue-400">
                  <Calendar size={18} />
                  <span className="font-medium">Versand planen</span>
                </div>
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                    Datum und Uhrzeit
                  </Label>
                  <Input
                    type="datetime-local"
                    value={formData.scheduled_at ? formData.scheduled_at.slice(0, 16) : ''}
                    onChange={(e) => handleChange('scheduled_at', e.target.value ? new Date(e.target.value).toISOString() : '')}
                    className="bg-background border-border rounded-none max-w-xs"
                    min={new Date().toISOString().slice(0, 16)}
                  />
                </div>
                <div className="flex gap-2">
                  <Button onClick={handleSchedule} disabled={saving || !formData.scheduled_at} className="gap-2 rounded-none">
                    {saving ? <Loader2 size={16} className="animate-spin" /> : <Clock size={16} />}
                    Versand planen
                  </Button>
                  <Button variant="outline" onClick={() => setShowSchedule(false)} className="rounded-none">
                    Abbrechen
                  </Button>
                </div>
              </div>
            ) : (
              <div className="bg-yellow-500/10 border border-yellow-500/30 p-4 space-y-4">
                <div className="flex items-start gap-2 text-yellow-500">
                  <AlertCircle size={18} className="flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium">Kampagne jetzt senden?</p>
                    <p className="text-sm text-muted-foreground mt-1">
                      Die E-Mail wird sofort an <strong>~{getTotalRecipients().toLocaleString('de-CH')} Empfänger</strong> gesendet.
                      Dieser Vorgang kann nicht rückgängig gemacht werden.
                    </p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button onClick={handleSendCampaign} disabled={sending} className="gap-2 rounded-none">
                    {sending ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle2 size={16} />}
                    Ja, jetzt senden
                  </Button>
                  <Button variant="outline" onClick={() => setShowSendConfirm(false)} disabled={sending} className="rounded-none">
                    Abbrechen
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Preview Modal */}
      {showPreview && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80">
          <div className="bg-card border border-border w-full max-w-6xl max-h-[90vh] flex flex-col">
            {/* Preview Header */}
            <div className="flex items-center justify-between p-4 border-b border-border">
              <div className="flex items-center gap-4">
                <Eye size={20} className="text-primary" />
                <h2 className="font-semibold">E-Mail Vorschau</h2>
              </div>
              
              {/* Device Toggle */}
              <div className="flex items-center gap-1 bg-muted p-1">
                <button
                  onClick={() => setPreviewDevice('desktop')}
                  className={`flex items-center gap-2 px-4 py-2 text-sm font-medium transition-colors ${
                    previewDevice === 'desktop' 
                      ? 'bg-primary text-primary-foreground' 
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                  data-testid="preview-device-desktop"
                >
                  <Monitor size={16} />
                  Desktop
                </button>
                <button
                  onClick={() => setPreviewDevice('tablet')}
                  className={`flex items-center gap-2 px-4 py-2 text-sm font-medium transition-colors ${
                    previewDevice === 'tablet' 
                      ? 'bg-primary text-primary-foreground' 
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                  data-testid="preview-device-tablet"
                >
                  <Tablet size={16} />
                  iPad
                </button>
                <button
                  onClick={() => setPreviewDevice('mobile')}
                  className={`flex items-center gap-2 px-4 py-2 text-sm font-medium transition-colors ${
                    previewDevice === 'mobile' 
                      ? 'bg-primary text-primary-foreground' 
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                  data-testid="preview-device-mobile"
                >
                  <Smartphone size={16} />
                  Mobile
                </button>
              </div>
              
              <button
                onClick={() => setShowPreview(false)}
                className="text-muted-foreground hover:text-foreground p-2"
                data-testid="close-preview"
              >
                <X size={20} />
              </button>
            </div>
            
            {/* Preview Content */}
            <div className="flex-1 overflow-auto p-6 bg-muted/30 flex justify-center">
              <div 
                className="bg-white shadow-lg transition-all duration-300"
                style={{ 
                  width: previewDevice === 'desktop' ? 630 : previewDevice === 'tablet' ? 768 : 375,
                  maxWidth: '100%'
                }}
              >
                {/* Email Preview Frame */}
                <div className="border-b-4 border-primary p-4 bg-white">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_shrt-portal/artifacts/mz9rlbeg_logo_shrt_email.png"
                    alt="S.H.R.T."
                    style={{ width: 100, height: 'auto' }}
                  />
                </div>
                
                <div className="p-6 bg-white">
                  {/* Subject Line */}
                  <div className="mb-4 pb-4 border-b border-gray-200">
                    <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Betreff</p>
                    <p className="font-semibold text-gray-900">{formData.subject || '(Kein Betreff)'}</p>
                  </div>
                  
                  {/* Email Content */}
                  <div 
                    style={{ 
                      fontFamily: 'Arial, Helvetica, sans-serif',
                      fontSize: 15,
                      lineHeight: 1.6,
                      color: '#333'
                    }}
                  >
                    <style>{`
                      .shrt-email-button-d5001c { 
                        background-color: #d5001c !important; 
                        padding: 18px 40px !important;
                      }
                      .shrt-email-button-1a1a1a { 
                        background-color: #1a1a1a !important; 
                        padding: 18px 40px !important;
                      }
                      .shrt-email-button { 
                        color: #ffffff !important; 
                        display: inline-block !important;
                      }
                    `}</style>
                    <div 
                      dangerouslySetInnerHTML={{ __html: formData.content_html || '<p style="color: #999;">Kein Inhalt</p>' }} 
                    />
                  </div>
                </div>
                
                {/* Footer */}
                <div className="bg-[#1a1a1a] p-6 text-center">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_shrt-portal/artifacts/mz9rlbeg_logo_shrt_email.png"
                    alt="S.H.R.T."
                    style={{ width: 40, height: 'auto', margin: '0 auto 12px' }}
                  />
                  <p className="text-white text-sm mb-1">Swiss Historic Racing Team</p>
                  <p className="text-xs text-gray-400">
                    <a href="https://www.shrt.ch" className="text-primary hover:underline">www.shrt.ch</a>
                  </p>
                  <p className="text-xs text-gray-500 mt-4">
                    © {new Date().getFullYear()} Swiss Historic Racing Team. Alle Rechte vorbehalten.
                  </p>
                </div>
                
                {/* Unsubscribe */}
                <div className="bg-gray-100 p-4 text-center">
                  <p className="text-xs text-gray-500">
                    Sie erhalten diesen Newsletter als Mitglied des SHRT.<br/>
                    <a href="#" className="text-gray-500 hover:underline">Newsletter abbestellen</a>
                  </p>
                </div>
              </div>
            </div>
            
            {/* Preview Footer */}
            <div className="flex items-center justify-between p-4 border-t border-border bg-card">
              <p className="text-sm text-muted-foreground">
                {previewDevice === 'desktop' && 'Desktop-Ansicht (630px)'}
                {previewDevice === 'tablet' && 'iPad-Ansicht (768px)'}
                {previewDevice === 'mobile' && 'Mobile-Ansicht (375px)'}
              </p>
              <Button onClick={() => setShowPreview(false)} className="rounded-none">
                Schliessen
              </Button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
